import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import {  
  Save, Search, Tag, Package, Box, List, Trash2, ArrowLeft, 
  Plus, X, RefreshCw, Layers, History, Paperclip, CreditCard, 
  ShoppingBag, Truck, ChevronLeft, ChevronRight, Zap, Workflow,
  Type, Minus, ShoppingCart, ArrowRightLeft
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import WorkflowActionModal from './WorkflowActionModal';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import SmartPrintButton from './PrintButton'; 
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';

const MRDetails = ({ siteId: propSiteId, orgId: propOrgId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  
  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || propSiteId || '';
  const sessionOrg = session.org_id || propOrgId || '';

  const { canCreate, canUpdate, canDelete } = usePermissions('mr');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [fieldMaps, setFieldMaps] = useState({});
  const [activeTab, setActiveTab] = useState('LINES');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
  const [wfContext, setWfContext] = useState({ can_start: false, can_action: false });
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

  const [data, setData] = useState({
    mrnum: '', description: '', status: 'OPEN', storeloc: '',
    siteid: sessionSite, orgid: sessionOrg, requireddate: '',
    requestby: session.username || '', lines: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '', filters: {}, targetIdx: null });

  const isReadOnly = data.status === 'APPROVED' || data.status === 'APPR' || data.status === 'CLOSE' || data.status === 'CAN' || data.status === 'CLOSED';
  const isEditable = !isReadOnly && (isNew ? canCreate : canUpdate);

  const loadData = useCallback(async () => {
    if (isNew) return;
    try {
      const token = localStorage.getItem('token');
      const resMap = await axios.get('/api/admin/field-map/materialrequest', {
          headers: { 'Authorization': `Bearer ${token}` }
      });
      setFieldMaps(resMap.data || {});

      setLoading(true);
      const res = await axios.get(`/api/inventory/mr/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
      });
      // 🎯 حماية المصفوفات
      res.data.lines = Array.isArray(res.data.lines) ? res.data.lines : [];
      setData(res.data);
    } catch (err) {
      toast.error(t('common.load_fail', 'Error loading data'));
    } finally {
      setLoading(false);
    }
  }, [id, isNew, t]);

  useEffect(() => {
    if (isNew) {
      setData({
        mrnum: '', description: '', status: 'OPEN', storeloc: '',
        siteid: session.site_id || '', orgid: session.org_id || '',
        requireddate: null, requestby: session.username || '', lines: []
      });
      if(session.org_id) {
          axios.get(`/api/inventory/mr/next-number/${session.org_id}`)
              .then(res => setData(prev => ({ ...prev, mrnum: res.data.next_mrnum })));
      }
    } else {
      loadData();
    }
  }, [id, isNew, loadData, session.site_id, session.org_id, session.username]);

  const loadNavigation = useCallback(async () => {
      const searchParams = new URLSearchParams(location.search);
      if (isNew) return;

      try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`/api/inventory/mr/${id}/navigation`, {
              params: {
                  all_sites: searchParams.get('all_sites') || "false", 
                  user_siteid: sessionSite,
                  orgid: sessionOrg,
                  filter_siteid: searchParams.get('filter_siteid') || "", 
                  mrnum: searchParams.get('mrnum') || "",
                  description: searchParams.get('description') || "",
                  status: searchParams.get('status') || "",
                  sortBy: searchParams.get('sortBy') || "mrnum",
                  sortDir: searchParams.get('sortDir') || "desc"
              },
              headers: { 'Authorization': `Bearer ${token}` }
          });
          setNav(res.data);
      } catch (err) { console.error("Nav failed", err); }
  }, [id, isNew, location.search, sessionSite, sessionOrg]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const fetchWFStatus = async () => {
    if (isNew) return;
    try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/check-runtime/MR/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setWfContext(res.data);
    } catch (err) { console.error("WF Check Failed", err); }
  };

  useEffect(() => { fetchWFStatus(); }, [id, data.status]);

  const fetchCount = useCallback(async () => {
    if (!data.mrnum || isNew) { setCount(0); return; }
    try {
      const res = await axios.get(`/api/attachments/count/MR/${data.mrnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Error fetching attachment count:", err); }
  }, [data.mrnum, isNew]);
  
  useEffect(() => { fetchCount(); }, [fetchCount]);

  const handleSearch = (fieldName, title, defaultType, lineIdx = null) => {
    if (isReadOnly) return;
    const linkedDomain = fieldMaps[fieldName];
    let extraFilters = { site_id: data.siteid, org_id: data.orgid };
    
    setLookup({
      isOpen: true, title: title, type: linkedDomain ? 'DOMAIN' : defaultType,
      domainId: linkedDomain || '', field: fieldName, filters: extraFilters, targetIdx: lineIdx
    });
  };

  const handleLookupSelect = (row) => {
    const val = row.itemnum || row.wonum || row.assetnum || row.location_id || row.value || row.id;
    if (lookup.targetIdx !== null) {
        const idx = lookup.targetIdx;
        setData(prev => {
            const nl = Array.isArray(prev.lines) ? [...prev.lines] : [];
            if (lookup.field === 'itemnum') {
                nl[idx].itemnum = val;
                nl[idx].description = row.description || row.item_ref?.description;
                nl[idx].unitcost = row.avgcost || 0;
            } else {
                nl[idx][lookup.field] = val;
            }
            return {...prev, lines: nl};
        });
    } else {
        setData(prev => ({...prev, [lookup.field]: val}));
    }
    setLookup(p => ({...p, isOpen: false}));
  };

  const handleSave = useCallback(async () => {
    if (isReadOnly) return toast.error(t('common.recordLocked', 'Record is locked and cannot be modified.'));
    if (!data.description ) return toast.error(t('common.descRequired', 'Description is required!'));
    if (!data.storeloc) return toast.error(t('mr.messages.storeRequired', 'Store is required!'));
    if (!data.requireddate) return toast.error(t('mr.messages.dateRequired', 'Required Date is required!'));
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`/api/inventory/mr/save`, data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(t('common.save_success'));
      if (isNew) navigate(`/mr/${res.data.mrnum}`);
      else loadData();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.save_fail')); } 
    finally { setLoading(false); }
  }, [data, isReadOnly, isNew, navigate, loadData, t]);

  const handleDelete = async () => {
      if (data.status !== 'OPEN') {
          return Swal.fire({ title: t('common.actionDenied', 'Action Denied'), text: t('common.cantDeleteStatus', `Cannot delete a record with status: ${data.status}`, {status: data.status}), icon: 'error', confirmButtonColor: '#1a3353' });
      }
  
      const result = await Swal.fire({
          title: t('common.are_you_sure'), text: t('mr.swal.deleteWarnText', 'This will permanently delete the Material Request and all its lines.'), icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#1a3353', confirmButtonText: t('common.delete')
      });
  
      if (result.isConfirmed) {
          try {
              const token = localStorage.getItem('token');
              await axios.delete(`/api/inventory/mr/${data.mrnum}`, { headers: { 'Authorization': `Bearer ${token}` } });
              toast.success(t('common.delete_success'));
              navigate('/mr'); 
          } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail')); }
      }
  };

  const handleRouteWorkflow = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/workflow/initiate', { module: 'MR', ref_id: id }, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(t('workflowAction.successMove', 'Workflow Initiated!'));
      loadData();
      fetchWFStatus();
    } catch (err) { toast.error(t('workflowAction.failedMove', 'Failed to route workflow')); }
  };

  // 🎯 دالة إنشاء PR أو Inventory Usage
  const generateFulfillment = async (docType) => {
    const loadingId = toast.loading(t('mr.messages.generating', `Generating ${docType}...`, {docType}));
    try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`/api/inventory/mr/${data.mrnum}/fulfill`, { type: docType }, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(t('mr.messages.generatedSuccess', `${docType} ${res.data.docnum} generated successfully!`, {docType, docnum: res.data.docnum}), { id: loadingId });
        // توجيه المستخدم للمستند الجديد
        if(docType === 'PR') navigate(`/pr/${res.data.docnum}`);
        if(docType === 'USAGE') navigate(`/inventoryusage/${res.data.docnum}`);
    } catch (err) {
        toast.error(err.response?.data?.detail || t('mr.messages.generateFail', `Failed to generate ${docType}`, {docType}), { id: loadingId });
    }
  };

  const addNewLine = (type) => {
    if (isReadOnly) return;
    setData(prev => {
        const currentLines = Array.isArray(prev.lines) ? prev.lines : [];
        const newLine = { mrlineid: null, linetype: type, itemnum: '', description: '', qty_requested: 1, unitcost: 0, glaccount: '', wonum: '', assetnum: '', location: '' };
        return { ...prev, lines: [...currentLines, newLine] };
    });
  };

  const updateLine = (idx, field, value) => {
    if (isReadOnly) return;
    setData(prev => {
        const newLines = Array.isArray(prev.lines) ? [...prev.lines] : [];
        if(newLines[idx]) newLines[idx][field] = value;
        return {...prev, lines: newLines};
    });
  };

  const removeLine = (idx) => {
    if (isReadOnly) return;
    setData(prev => {
        const newLines = Array.isArray(prev.lines) ? prev.lines : [];
        return {...prev, lines: newLines.filter((_, i) => i !== idx)};
    });
  };

  const goTo = (target) => target && navigate(`/mr/${target}${location.search}`);

  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);
  
  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);
  
  useEffect(() => {
    const openStatusModal = () => {
        if (isReadOnly && data.status === 'CAN') return Swal.fire({title: t('mr.swal.lockedTitle', 'Locked'), text: t('mr.swal.cantChangeCancel', 'Cancelled requests cannot change status'), icon: 'lock'});
        setShowStatusModal(true);
    };
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, [isReadOnly, data.status, t]);

  useEffect(() => {
    const handleOpenHistory = () => { setShowHistoryModal(true); };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => { window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory); };
  }, []);

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] text-[11px] overflow-hidden uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup {...lookup} onClose={() => setLookup({...lookup, isOpen: false})} onSelect={handleLookupSelect} />
      
      {showStatusModal && (
        <StatusModal 
          currentStatus={data.status} recordId={data.mrnum} appName="MR" domainId={data.status === 'APPR' ? 'CANCEL_ONLY' : fieldMaps['status']}
          onClose={() => setShowStatusModal(false)} onComplete={loadData}
        />
      )}
      {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="MR" recordId={data.mrnum} onClose={() => setShowHistoryModal(false)} />}
      {isWFHistoryOpen && <WorkflowHistoryModal module="MR" refId={id} onClose={() => setIsWFHistoryOpen(false)} />}
      
      {!isNew && (
        <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false);fetchCount();}} ownertable="MR" ownerid={data.mrnum} />
      )}

      {/* --- TOP TOOLBAR --- */}
      <div className={`bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0 `}>
        <div className={`flex items-center gap-3 `}>
          <button onClick={() => navigate('/mr')} className="p-1.5 hover:bg-white/10 rounded-full text-blue-200"><ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
          
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-orange-400">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 `}>
          {isEditable && (
          <button onClick={handleSave} disabled={loading || isReadOnly} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all active:scale-95 disabled:opacity-50">
            <Save size={14} /> {loading ? t('common.saving') : t('common.save')}
          </button>
          )}

          {wfContext.can_start && !isReadOnly && (
            <button onClick={handleRouteWorkflow} className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2 text-[11px] font-black h-8 shadow-md px-4 rounded-sm transition-all active:scale-95">
              <Zap size={14} fill="currentColor" /> {t('workflowAction.btnRoute')}
            </button>
          )}

          {data.status === 'APPR' && (
             <div className={`flex items-center gap-1 border border-white/20 bg-black/20 rounded p-0.5 shadow-inner `}>
                <button onClick={() => generateFulfillment('USAGE')} className="bg-teal-600 hover:bg-teal-500 px-3 py-1 rounded-sm font-black flex items-center gap-1 transition-all" title={t('mr.actions.createUsageDesc', 'Create Issue/Transfer Record')}>
                    <ArrowRightLeft size={12}/> {t('mr.actions.createUsage', 'CREATE USAGE')}
                </button>
                <button onClick={() => generateFulfillment('PR')} className="bg-blue-600 hover:bg-blue-500 px-3 py-1 rounded-sm font-black flex items-center gap-1 transition-all" title={t('mr.actions.createPRDesc', 'Create Purchase Request')}>
                    <ShoppingCart size={12}/> {t('mr.actions.createPR', 'CREATE PR')}
                </button>
             </div>
          )}

          <WorkflowActionModal moduleName="MR" refId={id} currentStatus={data.status} onActionComplete={loadData} />
          <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.history')}><Workflow size={16} className="text-orange-400"/></button>
          
          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          <SmartPrintButton moduleName="MR" refId={`${data.mrnum}`} />
          <button onClick={() => setShowStatusModal(true)} disabled={isReadOnly} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.change_status')}><Tag size={16}/></button>
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.attachments')}>
            <Paperclip size={16} />
            {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-in zoom-in">{count}</span>}
          </button>
          <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.history')}><History size={16}/></button>
          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 text-red-400 hover:bg-red-500/20 rounded-sm transition-colors" title={t('common.delete')}><Trash2 size={16}/></button>
          )}

          {/* Font Controls */}
          <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 shadow-inner ${isRTL ? 'mr-4 ' : 'ml-4'}`}>
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
              <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
            </div>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- HEADER FIELDS --- */}
      <div className={`bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-12 shadow-sm shrink-0 z-20 ${isRTL ?  'text-right ' : 'text-left'}`}>
        <HeaderField label={t('mr.fields.mrNum', 'MR Number')} value={data.mrnum} disabled={!isNew} onChange={v => setData({...data, mrnum: v})} width="w-24" isBold isRTL={isRTL} />
        <HeaderField label={t('assets.headers.desc', 'Description')} value={data.description} disabled={isReadOnly} onChange={v => setData({...data, description: v})} width="w-60" isRTL={isRTL}/>
        <HeaderField label={t('mr.fields.targetStore', 'Target Store')} value={data.storeloc} disabled={isReadOnly} onSearch={() => handleSearch('storeloc', t('mr.lookups.storerooms', 'Storerooms'), 'storerooms')} width="w-32" isRTL={isRTL} />
        <HeaderField label={t('mr.fields.reqDate', 'Required Date')} value={data.requireddate ? data.requireddate.substring(0, 10) : ''} disabled={isReadOnly} onChange={v => setData({...data, requireddate: v})} width="w-24" type="date" isRTL={isRTL} />
        <HeaderField label={t('assets.headers.status', 'Status')} value={data.status} disabled width="w-20" isRTL={isRTL} />
      </div>

      {/* --- TABS --- */}
      <div className={`flex bg-white border-b border-gray-100 px-4 shrink-0 font-black uppercase `}>
        <button onClick={() => setActiveTab('LINES')} className="px-8 py-3 flex items-center gap-2 transition-all tracking-tighter border-b-2 border-[#1a3353] text-[#1a3353] bg-gray-50">
          <Layers size={13}/> {t('mr.tabs.mrlines', 'REQUEST LINES')}
        </button>
      </div>

      {/* --- CONTENT --- */}
      <div className="flex-1 overflow-y-auto p-6 bg-[#f8f9fb] custom-scrollbar">
        <div className="bg-white border rounded shadow-2xl overflow-hidden min-h-[500px] max-w-7xl mx-auto">
            <div className={`bg-gray-50 p-3 border-b flex gap-4 px-8 items-center shadow-inner `}>
                <span className={`text-[0.9em] font-black text-blue-900 italic tracking-widest uppercase flex items-center gap-2 ${isRTL ? 'ml-auto ' : 'mr-auto'}`}>
                    <ShoppingBag size={14}/> {t('mr.sections.mrItems', 'Material Request Items')} {isReadOnly && t('common.readOnly', '(READ ONLY)')}
                </span>
                {!isReadOnly && (
                    <div className={`flex gap-2 `}>
                        <button onClick={() => addNewLine('ITEM')} className={`bg-blue-100 text-blue-700 px-4 py-1.5 rounded-sm font-black text-[9px] flex items-center gap-2 border border-blue-200 hover:bg-blue-700 hover:text-white transition-all active:scale-95 `}>
                          <Package size={12}/> {t('mr.actions.addMaterial', 'Add Material')}
                        </button>
                    </div>
                )}
            </div>

            {(Array.isArray(data.lines) ? data.lines : []).map((line, idx) => (
                <div key={idx} className="border-b last:border-0 hover:bg-blue-50/20 transition-all p-8 flex flex-col gap-6 animate-in fade-in duration-300">
                    
                    {/* SECTION 1: ITEM DATA */}
                    <div className={`grid grid-cols-12 gap-8 items-start ${isRTL ? 'dir-rtl' : ''}`}>
                        <div className="col-span-1 flex flex-col items-center justify-center pt-2">
                            <span className="w-7 h-7 rounded-full flex items-center justify-center text-[0.9em] font-black shadow-lg text-white bg-[#1a3353]">
                                {idx + 1}
                            </span>
                            {!isReadOnly && (
                                <button onClick={() => removeLine(idx)} className="mt-4 text-gray-300 hover:text-red-600 transition-colors">
                                    <Trash2 size={16}/>
                                </button>
                            )}
                        </div>
                        
                        <div className={`col-span-11 grid grid-cols-3 gap-8 bg-slate-50/50 p-5 rounded border border-slate-200 shadow-inner ${isRTL ? 'text-right dir-rtl' : 'text-left'}`}>
                            <div className="flex flex-col border-b border-gray-300 pb-1">
                                <label className="text-[0.9em] font-black text-gray-400 uppercase italic">{t('mr.tables.stockItem', 'Stock Item')}</label>
                                <div className={`flex justify-between items-center h-7 `}>
                                  <span className="font-black text-blue-900 truncate text-[12px]">{line.itemnum || t('common.select', 'SELECT...')}</span>
                                  {!isReadOnly && <Search size={14} className="text-blue-500 cursor-pointer hover:scale-110" onClick={() => handleSearch('itemnum', t('mr.lookups.inventory', 'Inventory Lookup'), 'items', idx)} />}
                                </div>
                                <span className="text-[0.9em] text-gray-400 truncate uppercase mt-1 italic font-bold">{line.description || t('common.noDescription', 'No description')}</span>
                            </div>

                            <div className="flex flex-col border-b border-blue-400 pb-1 bg-blue-50/30 px-2 rounded-t">
                                <label className="text-[0.9em] font-black text-blue-600 uppercase italic">{t('mr.tables.qtyReq', 'Qty Requested')}</label>
                                <input type="number" disabled={isReadOnly} className={`bg-transparent font-black text-blue-900 outline-none h-7 text-[1.0em] ${isRTL ? 'text-right' : 'text-left'}`} value={line.qty_requested} onChange={e => updateLine(idx, 'qty_requested', e.target.value)} />
                            </div>

                            <div className="flex flex-col border-b border-gray-300 pb-1">
                                <label className="text-[0.9em] font-black text-gray-400 uppercase font-mono">{t('mr.tables.estUnitCost', 'Est. Unit Cost')}</label>
                                <input type="number" disabled={isReadOnly} className={`bg-transparent font-black text-gray-700 outline-none h-7 ${isRTL ? 'text-right' : 'text-left'}`} value={line.unitcost} onChange={e => updateLine(idx, 'unitcost', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* SECTION 2: CHARGE TO INFO */}
                    <div className={`grid grid-cols-12 gap-8 items-start ${isRTL ? 'dir-rtl' : ''}`}>
                        <div className="col-span-1"></div>
                        <div className={`col-span-11 grid grid-cols-3 gap-8 bg-white border border-blue-200 p-6 rounded shadow-xl relative pt-8 transition-all ${isRTL ? 'text-right dir-rtl' : 'text-left'}`}>
                            <div className={`absolute -top-3 bg-[#1a3353] text-white px-5 py-1 text-[0.8em] font-black uppercase rounded shadow-lg tracking-[0.25em] flex items-center gap-2 ${isRTL ? 'right-6 ' : 'left-6'}`}>
                                <CreditCard size={10}/> {t('mr.sections.financialCharging', 'Financial Charging Details')}
                            </div>
                            
                            <div className="flex flex-col border-b border-gray-200">
                                <label className="text-[0.9em] font-black text-gray-400 uppercase italic">{t('mr.fields.chargeLoc', 'Charge Loc')}</label>
                                <div className={`flex justify-between items-center h-7 font-black text-blue-900 `}>
                                    <span className="truncate">{line.location || t('mr.placeholders.chooseLoc', 'CHOOSE LOC')}</span>
                                    {!isReadOnly && <Search size={14} className="text-blue-500 cursor-pointer" onClick={() => handleSearch('location', t('mr.lookups.locations', 'Site Locations'), 'locations', idx)} />}
                                </div>
                            </div>

                            <div className="flex flex-col border-b border-gray-200">
                                <label className="text-[0.9em] font-black text-gray-400 uppercase italic">{t('mr.fields.chargeAsset', 'Charge Asset')}</label>
                                <div className={`flex justify-between items-center h-7 font-black text-orange-700 `}>
                                    <span className="truncate">{line.assetnum || t('mr.placeholders.selectAsset', 'SELECT ASSET')}</span>
                                    {!isReadOnly && <Search size={14} className="text-orange-500 cursor-pointer" onClick={() => handleSearch('assetnum', t('mr.lookups.assets', 'Asset Registry'), 'assets', idx)} />}
                                </div>
                            </div>

                            <div className="flex flex-col border-b border-gray-200">
                                <label className="text-[0.9em] font-black text-gray-400 uppercase italic">{t('mr.fields.chargeWO', 'Charge WO')}</label>
                                <div className={`flex justify-between items-center h-7 font-black text-blue-600 `}>
                                    <span className="truncate">{line.wonum || t('mr.placeholders.chargeWO', 'CHARGE TO WO')}</span>
                                    {!isReadOnly && <Search size={14} className="text-blue-500 cursor-pointer" onClick={() => handleSearch('wonum', t('mr.lookups.workorders', 'Work Orders'), 'workorders', idx)} />}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            
            {(!data.lines || data.lines.length === 0) && (
              <div className="p-20 text-center text-gray-300 font-black italic tracking-widest uppercase">
                {t('mr.messages.noLines', 'No material lines added yet.')}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

// --- Helpers ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, onSearch, type = "text", options = [], isRTL }) => (
  <div className={`flex flex-col border-gray-200 px-4 last:border-0 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>
    <label className="text-[0.9em] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
    <div className={`flex items-center gap-2 `}>
      {type === "select" ? (
        <select 
            className={`${width} outline-none py-1 font-black uppercase text-blue-900 bg-transparent text-[11px] cursor-pointer ${isRTL ? 'dir-rtl text-right' : ''}`}
            value={value} disabled={disabled} onChange={e => onChange(e.target.value)}
        >
            {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      ) : (
        <input 
            type={type} 
            className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[13px] tracking-tighter' : 'text-blue-700 text-[12px]'} bg-transparent transition-all focus:border-blue-500 ${disabled ? 'opacity-50' : ''} ${isRTL ? 'text-right' : 'text-left'}`} 
            value={value || ''} 
            disabled={disabled} 
            onChange={e => onChange && onChange(e.target.value)} 
        />
      )}
      {(onSearch && !disabled) && <Search size={14} className="text-blue-500 cursor-pointer hover:scale-110 transition-transform" onClick={onSearch} />}
    </div>
  </div>
);

export default MRDetails;